export type SocialLink = {
  label: string;
  href: string;
  icon: "linkedin" | "github" | "email" | "website";
};

export type DeliveryStage = {
  title: string;
  text: string;
  skills: string[];
};

export type PublishedApp = {
  name: string;
  description: string;
  downloads?: string;
  storeUrl: string;
};

export type ExperienceItem = {
  role: string;
  company: string;
  location?: string;
  period: string;
  summary: string;
  highlights?: string[];
  appCount?: number;
};

export type ProjectItem = {
  title: string;
  context?: string;
  description: string;
  tags: string[];
  features?: string[];
  metric?: StatItem;
  image?: string;
  repoUrl?: string;
  liveUrl?: string;
};

export type StatItem = {
  value: number;
  prefix?: string;
  suffix?: string;
  label: string;
};

export type EducationItem = {
  title: string;
  institution: string;
  period: string;
  detail?: string;
  icon: "degree" | "school" | "security" | "course";
};

export const portfolio = {
  person: {
    fullName: "Cristian Henz Krein",
    headline: "Engenheiro de Software · Front-end",
    tagline:
      "Engenheiro de software com foco em React Native e React. Levo produtos financeiros da arquitetura até a publicação nas lojas, com observabilidade, feature flags e segurança no caminho.",
    location: "São Miguel do Oeste, SC · Brasil",
    profilePhoto: `${process.env.PUBLIC_URL ?? ""}/profile.png`,
    email: "cristian.krein@outlook.com",
    phoneDisplay: "(49) 99957-4664",
    phoneTel: "+5549999574664",
    websiteDisplay: "krein.dev",
    websiteUrl: "https://www.krein.dev",
    availability:
      "Aberto a conversas sobre produto digital, mobile e front-end em diferentes áreas.",
  },
  social: [
    {
      label: "Site",
      href: "https://www.krein.dev",
      icon: "website",
    },
    {
      label: "LinkedIn",
      href: "https://br.linkedin.com/in/cristian-henz-krein-9a0a45190",
      icon: "linkedin",
    },
    {
      label: "GitHub",
      href: "https://github.com/cristiankrein",
      icon: "github",
    },
    {
      label: "E-mail",
      href: "mailto:cristian.krein@outlook.com",
      icon: "email",
    },
  ] satisfies SocialLink[],
  honors: [
    "Menção honrosa na Olimpíada Brasileira de Matemática (OBM), Nível 3, 2017.",
  ],
  stats: [
    { value: 60, suffix: "+", label: "Apps em produção (white-label)" },
    { value: 600, suffix: "+", label: "Usuários no Localy Tickets" },
    { value: 7, suffix: "+", label: "Anos em tecnologia" },
  ] as StatItem[],
  about: {
    paragraphs: [
      "Sou engenheiro de software focado em produtos mobile e web construídos para durar: da arquitetura ao deploy em produção, passando por performance, observabilidade e segurança. Atuo principalmente com React e React Native, boa parte da carreira em fintechs e ambientes regulados, hoje aberto também a outros segmentos que valorizem entrega de ponta a ponta.",
      "Gosto de problemas onde a decisão técnica tem consequência real: uma arquitetura mal pensada em pagamentos, ou num app usado por dezenas de marcas, custa caro depois. Por isso priorizo simplicidade, medir antes de otimizar, e deixar rastro (observabilidade, feature flags, testes) para que qualquer mudança em produção seja reversível.",
    ],
    approach: [
      {
        title: "Ownership de ponta a ponta",
        text: "Acompanho a decisão até o impacto em produção, não só até o merge.",
      },
      {
        title: "Risco calculado",
        text: "Em contexto regulado, mudar sem observabilidade é aposta. Prefiro rollback fácil a torcer pra dar certo.",
      },
      {
        title: "Comunicação sem ruído",
        text: "Traduzo decisão técnica em impacto de produto, pra quem paga a conta e pra quem mantém depois de mim.",
      },
    ],
  },
  delivery: {
    stages: [
      {
        title: "Arquitetura",
        text: "Uma base única e componentes reutilizáveis, pensados para aguentar dezenas de marcas sem virar dezenas de projetos.",
        skills: ["React Native", "Expo", "React", "TypeScript / JavaScript", "Chakra UI"],
      },
      {
        title: "Qualidade",
        text: "Fluxo crítico testado antes de chegar no usuário, do componente isolado ao teste de carga.",
        skills: ["Testes E2E", "Jest / RTL", "K6 (carga / stress)", "ESLint"],
      },
      {
        title: "Release",
        text: "Versionamento, build e envio para as lojas automatizados, com controle individual por cliente.",
        skills: ["CI/CD", "Git", "Deploy Google Play", "Deploy App Store"],
      },
      {
        title: "Produção",
        text: "Toda mudança em produção precisa ser visível e reversível: monitorar, liberar aos poucos, voltar atrás rápido.",
        skills: ["Sentry", "Feature flags", "OneSignal"],
      },
    ] satisfies DeliveryStage[],
    domain: {
      title: "Pagamentos e ambientes sensíveis",
      text: "O contexto onde boa parte disso foi exercitado, do frente de caixa de supermercado ao app financeiro.",
      skills: ["Pix", "Pagamentos / cartões", "PDV / SITEF (contexto varejo)", "PostgreSQL"],
    },
  },
  experience: [
    {
      role: "Software Engineer",
      company: "ONZ Finance",
      location: "Brasil",
      period: "Set 2022, atual",
      summary:
        "Responsável pelo desenvolvimento, evolução e sustentação de aplicativo mobile no setor financeiro, de arquitetura e automações até builds e publicação nas lojas Google Play e App Store.",
      highlights: [
        "Ecossistema white-label com mais de 60 aplicativos sobre React Native e Expo, com arquitetura escalável e reuso de componentes.",
        "Pipelines de CI/CD integrados ao Expo: versionamento, builds, distribuição e upload automatizado para as lojas.",
        "Gestão de fluxos de build e release e versionamento individualizado por cliente via plataforma própria de controle.",
        "Observabilidade com Sentry; feature flags para exposição gradual; push com OneSignal.",
        "Participação em estratégias de segurança e prevenção a fraudes e em funcionalidades de pagamento (Pix, cartões, transferências).",
      ],
      appCount: 60,
    },
    {
      role: "Analista de Controle de Qualidade Júnior",
      company: "Sysmo Sistemas LTDA",
      location: "Brasil",
      period: "Mai 2022, Ago 2022",
      summary:
        "Criação e execução de testes automatizados para fluxos críticos, incluindo E2E, testes de carga e performance/stress.",
      highlights: [
        "Uso de K6 para testes de performance; maior parte dos testes em JavaScript cobrindo fluxos do projeto web no varejo.",
      ],
    },
    {
      role: "Analista de Suporte Técnico Pleno",
      company: "Sysmo Sistemas LTDA",
      location: "Brasil",
      period: "Out 2021, Abr 2022",
      summary:
        "Suporte técnico avançado e projetos de infraestrutura para clientes de PDV e pagamentos.",
      highlights: [
        "Participação na migração de servidores SITEF para ambientes em nuvem.",
        "Implantação de novos clientes, da configuração de servidores ao go-live do frente de caixa.",
        "Treinamentos e apresentações para clientes sobre o software e processos comerciais/fiscais.",
      ],
    },
    {
      role: "Analista de Suporte Técnico Júnior",
      company: "Sysmo Sistemas LTDA",
      location: "Brasil",
      period: "Out 2020, Set 2021",
      summary:
        "Suporte a PDV de supermercados: impressoras fiscais, pinpads (POS), pagamentos e configurações avançadas.",
      highlights: [
        "Atuação com integração PIX nas estações de caixa e cartões via SITEF.",
      ],
    },
    {
      role: "Analista de Apoio ao Usuário",
      company: "Sysmo Sistemas LTDA",
      location: "Brasil",
      period: "Abr 2019, Set 2020",
      summary:
        "Atendimento e diagnóstico técnico em sistemas de frente de caixa e pagamentos.",
      highlights: [
        "Consultas e análises em PostgreSQL para resolver incidentes operacionais.",
      ],
    },
  ] satisfies ExperienceItem[],
  projects: [
    {
      title: "Localy Tickets",
      context: "Projeto pessoal",
      description:
        "Plataforma web própria para venda de ingressos de eventos, construída do zero e mantida em produção, com entrada contínua de novos eventos.",
      tags: ["React", "Node.js", "Pix", "Projeto pessoal"],
      features: [
        "Cadastro de eventos",
        "Checkout com Pix e cartão",
        "Ingresso digital com QR Code",
        "Fluxo de reembolso",
      ],
      metric: { value: 600, suffix: "+", label: "usuários cadastrados" },
      image: `${process.env.PUBLIC_URL ?? ""}/projects/localy-tickets.png`,
      liveUrl: "https://eventos.portal-localy.com",
    },
    {
      title: "Festança Decorações",
      context: "Freelance para cliente",
      description:
        "Site e catálogo online de uma empresa de decoração de festas, feito como freelancer: o cliente apresenta as decorações por tipo de evento e recebe o pedido de orçamento direto no WhatsApp.",
      tags: ["React", "Catálogo online", "Freelance"],
      features: [
        "Catálogo organizado em 12 categorias de evento",
        "Ordenação por preço, novidade e popularidade",
        "Página de detalhes para cada decoração",
        "Contato direto pelo WhatsApp",
      ],
      metric: { value: 270, suffix: "+", label: "decorações no catálogo" },
      image: `${process.env.PUBLIC_URL ?? ""}/projects/festanca-decoracoes.png`,
      liveUrl: "https://festancadecoracoes.com.br",
    },
  ] as ProjectItem[],
  // Only what each store listing already shows publicly: no logos, no client-specific details.
  publishedApps: [
    {
      name: "ModoBank",
      description: "Conta digital PJ para empresas movimentarem e cobrarem pelo app.",
      downloads: "5 mil+ downloads",
      storeUrl: "https://play.google.com/store/apps/details?id=com.modobank&hl=pt_BR",
    },
    {
      name: "Âmplea Coop",
      description: "App de cooperativa de crédito: conta corrente, Pix, TED e pagamentos.",
      downloads: "1 mil+ downloads",
      storeUrl: "https://play.google.com/store/apps/details?id=br.coop.sulcredi.business&hl=pt_BR",
    },
  ] satisfies PublishedApp[],
  education: [
    {
      title: "Graduação em Ciência da Computação",
      institution: "Universidade do Oeste de Santa Catarina (UNOESC)",
      period: "Jan 2019, Jun 2023",
      detail:
        "Base em ciência da computação aplicada a desenvolvimento de software e sistemas.",
      icon: "degree",
    },
    {
      title: "Ensino médio completo",
      institution: "Escola de Educação Básica Madre Benvenuta",
      period: "Concluído em Dez 2018",
      detail: "Ensino médio regular concluído antes da entrada na universidade.",
      icon: "school",
    },
    {
      title: "Introdução à segurança da informação",
      institution: "eSecurity CYBER SECURITY",
      period: "Set 2021",
      detail: "Fundamentos de segurança aplicados ao contexto corporativo.",
      icon: "security",
    },
    {
      title: "Curso Descomplifica (12 meses)",
      institution: "Descomplifica",
      period: "Jan 2018, Dez 2018",
      detail: "Trilha online intensiva de desenvolvimento e boas práticas.",
      icon: "course",
    },
  ] satisfies EducationItem[],
};
